import * as React from 'react';
import {graphql} from 'react-relay';

/**
 * Detail query for the slug-based route. The query boundary now lives in the
 * route via the Forecast seam's `ForecastQueryRenderer` (ACC-460); this module
 * owns the operation document and a small fetch-key helper, mirroring
 * `useTicketListQuery.ts`.
 *
 * `viewer { actualPersonId }` is required by `ForecastQueryRenderer`'s
 * logged-in guard.
 */
export const TicketDetailBySlugQuery = graphql`
	query useTicketDetailBySlugQuery($slug: String!) {
		viewer {
			actualPersonId
			ticketDetailBySlug(slug: $slug) {
				ticket {
					id
					slug
					subject
					description
					status
					priority
					channel
					assigneeId
					assignee {
						fullName
						...ticketingPerson_person
					}
					requester {
						id
						name
						email
					}
					createdAt
					updatedAt
					closedAt
					links {
						__typename
						linkType
						targetTicket {
							id
						}
						targetProject {
							id
						}
					}
					...TicketDetailHeader_ticket
					...TicketMetaPanel_ticket
					...RelatedTab_ticket
					...LinkedTasksTab_ticket
					...TimeTab_ticket
					...WatchersList_ticket
					...AiSummaryBlock_ticket
					...AiSuggestedActionsList_ticket
					linkedTasks {
						id
					}
					ticketTimeRegistrations {
						id
					}
				}
				timeline {
					...ActivityTimelineItem_activity
					...ActivityTab_activity
					...StreamTab_activity
				}
				attachments {
					id
					filename
					contentType
					sizeBytes
					downloadUrl
					uploadedBy {
						# FOR-261: the own-vs-others delete gate compares this against the
						# viewer. Must stay in step with useTicketDetail's query — both feed
						# the same TicketDetailContent, whose Attachments type is derived
						# from the by-id query alone, so a divergence is not type-caught.
						id
						fullName
						...ticketingPerson_person
					}
					uploadedByName
					createdAt
				}
			}
		}
	}
`;

export function useTicketDetailBySlugFetchKey() {
	const [fetchKey, setFetchKey] = React.useState(0);
	const refetch = React.useCallback(() => setFetchKey(k => k + 1), []);
	return {fetchKey, refetch};
}
